import React, { useEffect, useMemo, useState } from "react";
import {
  listCreditors,
  createCreditor,
  deleteCreditor,
} from "../../api";

const PANEL =
  "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-6 shadow-[0_12px_30px_rgba(2,6,23,0.18)]";
const INPUT =
  "h-11 w-full rounded-xl border border-[#1f2638] bg-[#0f1523] px-4 text-sm text-slate-200 placeholder:text-slate-500 outline-none transition focus:border-violet-500/40 focus:ring-2 focus:ring-violet-600/40 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_PRIMARY =
  "inline-flex h-11 w-full items-center justify-center rounded-xl border border-violet-600/40 bg-violet-600/20 px-5 text-sm font-semibold text-white transition hover:bg-violet-600/30 focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
  "inline-flex items-center justify-center rounded-xl border border-[#263048] bg-transparent px-3 py-2 text-sm font-semibold text-slate-200 transition hover:bg-[#0e1320] disabled:cursor-not-allowed disabled:opacity-60";
const DELETE_BTN =
  "inline-flex items-center gap-2 rounded-xl px-2 py-2 text-sm font-medium text-slate-400 transition hover:bg-[#0f1523] hover:text-rose-300 disabled:cursor-not-allowed disabled:opacity-60";
const SECTION_LABEL = "text-xs font-semibold uppercase tracking-wider text-slate-400";
const FIELD_LABEL = "mb-3 text-xs font-semibold uppercase tracking-wider text-slate-400";

function TrashIcon(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
      <path
        d="M9 3h6m-9 4h12m-1 0-.7 10.2a2 2 0 0 1-2 1.8H9.7a2 2 0 0 1-2-1.8L7 7m3 4v4m4-4v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function InstitutionTypeSelector({
  isBank,
  onChange,
  disabled = false,
}) {
  const options = [
    { key: "bank", label: "БАНКОВИ", value: true },
    { key: "nonbank", label: "НЕБАНКОВИ", value: false },
  ];

  return (
    <div className="grid h-11 grid-cols-2 gap-1 rounded-xl border border-[#1f2638] bg-[#0f1523] p-1">
      {options.map((option) => {
        const active = isBank === option.value;
        const tone = option.value
          ? "data-[active=true]:bg-violet-600/20 data-[active=true]:text-violet-100"
          : "data-[active=true]:bg-emerald-500/15 data-[active=true]:text-emerald-100";

        return (
          <button
            key={option.key}
            type="button"
            data-active={active}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={[
              "rounded-[10px] px-3 text-xs font-semibold uppercase tracking-[0.12em] text-slate-500 transition",
              "hover:text-slate-200 disabled:cursor-not-allowed disabled:opacity-60",
              tone,
            ].join(" ")}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

function CreditorTypeBadge({ isBank }) {
  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]",
        isBank
          ? "border-violet-500/20 bg-violet-500/12 text-violet-200"
          : "border-emerald-500/20 bg-emerald-500/12 text-emerald-200",
      ].join(" ")}
    >
      {isBank ? "Банков" : "Небанков"}
    </span>
  );
}

export default function CreditorsAdmin({ onBack }) {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [name, setName] = useState("");
  const [isBank, setIsBank] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      try {
        setErr("");
        setLoadingList(true);
        const arr = await listCreditors();
        if (!alive) return;
        setItems(arr || []);
      } catch (e) {
        if (!alive) return;
        setItems([]);
        setErr(e?.message || "Грешка при зареждане на кредитори");
      } finally {
        if (alive) setLoadingList(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [reloadKey]);

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), "bg", {
        sensitivity: "base",
      })
    );
  }, [items]);

  const submitCreate = async () => {
    const trimmed = String(name || "").trim();
    if (!trimmed) {
      setToast("Попълни име на кредитор.");
      return;
    }

    try {
      setCreating(true);
      setErr("");
      await createCreditor({ name: trimmed, is_bank: isBank });
      setName("");
      setIsBank(true);
      setToast("Кредиторът е добавен.");
      setReloadKey((k) => k + 1);
    } catch (e) {
      setToast(e?.message || "Грешка при създаване");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (item) => {
    const creditorId = String(item?.id || "").trim();
    const creditorName = String(item?.name || "").trim();
    if (!creditorName || !creditorId) {
      setToast("Липсва идентификатор на кредитора.");
      return;
    }

    const ok = window.confirm(`Сигурен ли си, че искаш да изтриеш кредитора "${creditorName}"?`);
    if (!ok) return;

    try {
      setDeletingId(creditorId);
      setErr("");
      await deleteCreditor(creditorId);
      setToast("Кредиторът е изтрит.");
      setReloadKey((k) => k + 1);
    } catch (e) {
      setToast(e?.message || "Грешка при изтриване");
    } finally {
      setDeletingId("");
    }
  };

  const formBusy = creating || loadingList;

  return (
    <div className="p-6 space-y-8">
      <div className="flex flex-wrap items-start gap-4">
        <div className="min-w-0 flex-1">
          <h1 className="text-3xl font-bold tracking-tight text-slate-100">Кредитори</h1>
        </div>

        {typeof onBack === "function" && (
          <button className={BTN_GHOST} onClick={onBack} disabled={formBusy}>
            Назад
          </button>
        )}
      </div>

      <section className={PANEL}>
        <div className="text-xl font-semibold text-slate-100">Добавяне на кредитор</div>

        <div className="mt-8 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(280px,0.95fr)_250px]">
          <div>
            <div className={FIELD_LABEL}>Име на кредитора</div>
            <input
              className={INPUT}
              placeholder="Въведете име"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitCreate();
                }
              }}
              disabled={creating}
            />
          </div>

          <div>
            <div className={FIELD_LABEL}>Тип институция</div>
            <InstitutionTypeSelector
              isBank={isBank}
              onChange={setIsBank}
              disabled={creating}
            />
          </div>

          <div className="xl:self-end">
            <button className={BTN_PRIMARY} onClick={submitCreate} disabled={creating}>
              {creating ? "Добавяне..." : "Добави"}
            </button>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div className={SECTION_LABEL}>Списък на кредиторите</div>

        {err && (
          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 px-5 py-4 text-sm text-rose-200">
            {err}
          </div>
        )}

        <div className="space-y-3">
          {loadingList && !sortedItems.length ? (
            <div className="rounded-2xl border border-[#1f2638] bg-[#121825] px-5 py-5 text-sm text-slate-300">
              Зареждане на кредитори...
            </div>
          ) : !sortedItems.length ? (
            <div className="rounded-2xl border border-[#1f2638] bg-[#121825] px-5 py-5 text-sm text-slate-300">
              Няма добавени кредитори.
            </div>
          ) : (
            sortedItems.map((item, index) => {
              const creditorId = String(item?.id || "").trim();
              const creditorName = String(item?.name || "").trim();
              const rowBusy = deletingId === creditorId;

              return (
                <div
                  key={creditorId || creditorName || index}
                  className="rounded-2xl border border-[#1f2638] bg-[#121825] px-5 py-5 shadow-[0_10px_24px_rgba(2,6,23,0.12)]"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                    <div className="flex min-w-0 flex-1 items-center gap-4">
                      <div className="w-10 shrink-0 text-lg font-semibold tracking-[0.16em] text-slate-300">
                        {String(index + 1).padStart(2)}
                      </div>

                      <div className="min-w-0 truncate text-xl font-semibold text-white">
                        {creditorName || "—"}
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 lg:justify-end">
                      <CreditorTypeBadge isBank={!!item?.is_bank} />

                      <button
                        type="button"
                        className={DELETE_BTN}
                        onClick={() => handleDelete(item)}
                        disabled={rowBusy || !creditorId}
                      >
                        <TrashIcon />
                        <span>{rowBusy ? "Изтриване..." : "Изтрий"}</span>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      {toast && (
        <div className="fixed top-6 left-1/2 z-[60] -translate-x-1/2">
          <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 text-sm text-slate-200 shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
